import React, {
	useState,
	useEffect,
	useCallback,
	useRef,
	memo,
	useMemo,
} from "react";

import { eventTypes, throttleRates } from "../../hooks/useTrack";

import { TrackingContext } from ".";
import useData from "../../hooks/useData";
import uuid4 from "../../utils/uuid4";
import useMode from "../../hooks/useMode";
import { VIEWS, MODES } from "../../conf";
import store from "../../utils/store";
import useVisibleGroups from "../../hooks/useVisibleGroups";
import GET from "../../utils/GET";
import useIdentity from "../../hooks/useIdentity";
import useExperience from "../../hooks/useExperience";
import useDimensions from "../../hooks/useDimensions";
import { eventRouter } from "../../utils/snowplowEventMigrator";
// import { WIDGET_DEBUG_TARGET } from "../../event_conf";

const TrackingManager = memo(({ children }) => {
	const data = useData();

	const { canTrack } = useMemo(() => data, [data]);
	const { inPPE, isCollapsed, mode } = useMode();

	const widgetMode = useMemo(
		() => Object.keys(MODES).find((key) => MODES[key] === mode),
		[mode]
	);

	const lastProc = useRef(-1);
	const pageLoadFired = useRef(false);
	// const debugWidgetLoadFired = useRef(false);

	const { hasIdentity, user } = useIdentity();
	const { activeWidgetVersion, activeWidgetType, runningExperiments } =
		useExperience();

	const { parentInnerWidth, parentInnerHeight, innerWidth, innerHeight } =
		useDimensions();

	const {
		frame: { inViewPort },
	} = useVisibleGroups();

	const processEvent = useCallback(
		({ name, properties = {} }, id) => {
			if (
				id <= lastProc.current ||
				(!inViewPort &&
					name !== eventTypes.PAGE_LOAD &&
					name !== eventTypes.WIDGET_LOAD &&
					name !== eventTypes.WIDGET_DEBUG && // manual override to allow event to fire in any conditions
					GET("surfaceIdx") !== "coop-overlay") ||
				isCollapsed
			) {
				return;
			}

			if (name in throttleRates) {
				const key = name + "-last";
				let last = store.getItem(key);
				// Update new time
				const now = new Date().getTime();
				store.setItem(key, now);

				if (last && !isNaN(last)) {
					last = Number(last);
					if (now - last <= throttleRates[name]) {
						console.log(
							`%c[ IGNORED ] %c ${name} %c Last fired ${
								(now - last) / 1000
							}s ago | Allowed gap: ${
								throttleRates[name] / 1000
							}s`,
							"background-color: #ff5081; color: white; font-weight: 500;",
							"background-color: #433d7a; color: white; font-weight: 700;",
							"background-color: #5076ff; color: white; font-weight: 500;"
						);

						return;
					}
				}
			}

			const baseProperties = {
				coop_session: data?.coopSession ? data?.coopSession : data?.s,
				publisher: data?.publisher,
				publisher_name: data?.publisher_details?.name,
				categories: data?.c
					? data.c
					: Array.isArray(data?.publisher_details?.categories)
					? data.publisher_details.categories.map(({ name }) => name)
					: undefined,
				url: window.location.href,
				host: data?.scrapedInformation?.host,
				shipping_id: data?.scrapedInformation?.shipping_id,
				billing_id: data?.scrapedInformation?.billing_id,
				customer_id: data?.scrapedInformation?.customer_id,
				created_at: data?.scrapedInformation?.created_at,
				order_id: data?.scrapedInformation?.identifier,
				confirmation_id: data?.scrapedInformation?.confirmation_id,
				cost: data?.scrapedInformation?.cost,
				customer_type: data?.customer_type,
				shopify_order_id: data?.scrapedInformation?.shopify_order_id,
				contact_email: data?.scrapedInformation?.contactEmail,
				userName: data?.scrapedInformation?.name,
				total_modules:
					data?.display_order && Array.isArray(data?.display_order)
						? data?.display_order?.length
						: -1,
				post_purchase: inPPE,

				survey: data?.publisher_details?.survey,
				upsell: data?.publisher_details?.upsells_enabled,
				shopify_shop: data?.publisher_details?.shopify_shop || null,
				is_shopify_shop: !!data?.publisher_details?.shopify_shop,

				widget_type: activeWidgetType,
				widget_mode: widgetMode || null,
				widget_version: activeWidgetVersion,
				running_experiments: runningExperiments,
				coop_event_id: uuid4(),
				device_width: parentInnerWidth,
				device_height: parentInnerHeight,
				frame_width: innerWidth,
				frame_height: innerHeight,
				has_identity: hasIdentity,
				recommendation_id: data?.recommendation_id,
				is_anon_recommendation: data?.is_anon_recommendation,
				user,
				page_type: data?.page_type,
				...(data?.global_event_metadata || {}),
			};

			const allProperties = {
				...baseProperties,
				...properties,
			};

			eventRouter({
				name,
				baseProperties,
				eventProperties: properties,
				allProperties,
			});

			if (typeof id === "number") lastProc.current = id;

			return true;
		},
		[
			data,
			inPPE,
			inViewPort,
			hasIdentity,
			isCollapsed,
			activeWidgetType,
			activeWidgetVersion,
			runningExperiments,
			user,
			parentInnerWidth,
			parentInnerHeight,
			innerWidth,
			innerHeight,
		]
	);

	const [queue, setQueue] = useState([]);
	const push = useCallback((event) => {
		setQueue((queue) => [...queue, event]);
	}, []);

	useEffect(() => {
		const {
			scrapedInformation: { purchases } = {},
			load_metadata: loadMetadata = {},
			view,
		} = data;
		// ADDING TEMP CONDITION FOR view === 'LEAD_GEN' to support old script cache
		if (
			(view !== VIEWS.WIDGET && view !== "LEAD_GEN") ||
			pageLoadFired.current
		) {
			return;
		}
		processEvent({
			name: eventTypes.PAGE_LOAD,
			properties: {
				purchases,
				...loadMetadata,
			},
		});
		pageLoadFired.current = true;
	}, [data, processEvent]);

	useEffect(() => {
		if (canTrack && queue.length > 0) {
			queue.forEach(processEvent);
		}
	}, [canTrack, queue, processEvent]);

	return (
		<TrackingContext.Provider value={push}>
			{children}
		</TrackingContext.Provider>
	);
});

export default TrackingManager;
